/**
 * Prices Page - Step 4 of Five-Step Workflow
 *
 * This page allows users to review, approve, and manage calculated prices
 * before they are exported to external systems.
 *
 * Tabs:
 * 1. Overview - Dashboard showing price health, pending approvals, exceptions
 * 2. Price Updates - Review and approve/reject pending price changes
 * 3. Current Prices - Searchable, filterable list of all active prices
 * 4. Price History - Audit trail of all price changes with rollback capability
 *
 * @see /docs/frontend-specs/prices-page-interface.md
 */

import { useState } from 'react'
import { Card, Tabs, Spin } from 'antd'
import {
  DollarOutlined,
  CheckCircleOutlined,
  TableOutlined,
  HistoryOutlined,
} from '@ant-design/icons'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { NoOrgSelectedPrompt } from '@/components/NoOrgSelectedPrompt'
import { OverviewTab } from './components/OverviewTab'
import { PriceUpdatesTab } from './components/PriceUpdatesTab'
import { CurrentPricesTab } from './components/CurrentPricesTab'
import { PriceHistoryTab } from './components/PriceHistoryTab'
import type { PricesPageState } from './types'

export default function PricesPage() {
  // Check for org context to prevent RLS errors
  const { data: user, isLoading: isUserLoading } = useCurrentUser()
  const hasOrgContext = !!user?.currentOrganization

  const [state, setState] = useState<PricesPageState>({
    activeTab: 'overview',
    selectedRows: [],
    updateDrawerOpen: false,
    selectedUpdate: null,
    historyDrawerOpen: false,
    selectedHistoryEntry: null,
    effectiveDateModalOpen: false,
  })

  const handleTabChange = (key: string) => {
    setState((prev) => ({
      ...prev,
      activeTab: key as PricesPageState['activeTab'],
    }))
  }

  const handleNavigateToUpdates = () => {
    setState((prev) => ({ ...prev, activeTab: 'price-updates' }))
  }

  const tabItems = [
    {
      key: 'overview',
      label: (
        <span>
          <DollarOutlined />
          Overview
        </span>
      ),
      children: <OverviewTab onNavigateToUpdates={handleNavigateToUpdates} />,
    },
    {
      key: 'price-updates',
      label: (
        <span>
          <CheckCircleOutlined />
          Price Updates
        </span>
      ),
      children: <PriceUpdatesTab />,
    },
    {
      key: 'current-prices',
      label: (
        <span>
          <TableOutlined />
          Current Prices
        </span>
      ),
      children: <CurrentPricesTab />,
    },
    {
      key: 'history',
      label: (
        <span>
          <HistoryOutlined />
          Price History
        </span>
      ),
      children: <PriceHistoryTab />,
    },
  ]

  // Loading state
  if (isUserLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    )
  }

  // No organization selected - show friendly prompt
  if (!hasOrgContext) {
    return <NoOrgSelectedPrompt featureName="prices" />
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <span style={{ fontSize: '20px', fontWeight: 600 }}>
            <DollarOutlined style={{ marginRight: '8px' }} />
            Prices
          </span>
        }
        extra={
          <span style={{ fontSize: '14px', color: '#8c8c8c' }}>
            Step 4 of 5-Step Workflow
          </span>
        }
      >
        <Tabs
          activeKey={state.activeTab}
          onChange={handleTabChange}
          items={tabItems}
          size="large"
        />
      </Card>
    </div>
  )
}
